import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  useGetCurrentUserProfileQuery,
  useUpdateCurrentUserProfileMutation,
} from '@app/store/slices/users/usersApi';
import { useDateOfBirthInput } from './useDateOfBirthInput';
import { useApiErrorHandler } from './useApiErrorHandler';
import { useDisplayError } from './useDisplayError';
import { formatBirthday, parseBirthday } from '@app/shared/utils/birthday';
import { Page } from '@app/routes';
import { Gender } from '@app/shared/types/common';
import { useGetEnergyStoneInfo } from '@app/shared/hooks/useGetEnergyStoneInfo';

export function useUserDataForm() {
  const { energyStones } = useGetEnergyStoneInfo();
  const [params] = useSearchParams();
  const stoneId = params.get('stoneId');
  const { data: userProfile, isError: isUserProfileError } = useGetCurrentUserProfileQuery();
  const [updateCurrentUserProfile, { isLoading: isUpdatingCurrentUserProfile }] =
    useUpdateCurrentUserProfileMutation();
  const {
    dateOfBirth,
    setDateOfBirth,
    dateOfBirthErrorTranslationKey,
    handleDateOfBirthChange,
    handleDateOfBirthClear,
    validateDateOfBirth,
  } = useDateOfBirthInput();
  const { handleFetchError } = useApiErrorHandler();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [gender, setGender] = useState<Gender>('f');

  useDisplayError(isUserProfileError, t('errors.failed_to_load_user'));

  useEffect(() => {
    if (!userProfile) {
      return;
    }

    if (userProfile.gender) {
      setGender(userProfile.gender);
    }
    if (userProfile.birthday) {
      const parsedBirthday = parseBirthday(userProfile.birthday);
      setDateOfBirth(parsedBirthday);
    }
  }, [userProfile]);

  const handleFormSubmit = async () => {
    if (!validateDateOfBirth() || !userProfile) {
      return;
    }

    try {
      const { day, month, year } = dateOfBirth;
      const formattedBirthday = formatBirthday(day, month, year);

      await updateCurrentUserProfile({ birthday: formattedBirthday, gender }).unwrap();
      if (energyStones.length) {
        const stoneToRedirect = energyStones.find((stone) => Number(stoneId) === stone.id);

        const firstStoneAbilityIndex = stoneToRedirect?.abilities[0].matrixIndex;
        void navigate(
          `/matrix/channels/${String(stoneToRedirect?.id)}/${String(firstStoneAbilityIndex)}`
        );
      } else {
        void navigate(Page.HOME);
      }
    } catch (err) {
      handleFetchError(err);
    }
  };

  return {
    isUpdatingCurrentUserProfile,
    dateOfBirth,
    dateOfBirthErrorTranslationKey,
    handleDateOfBirthChange,
    handleDateOfBirthClear,
    gender,
    setGender,
    handleFormSubmit,
  };
}
